import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { PosStore } from "@/utils/posStores";
import { readLegacyPinIds, clearLegacyPins } from "@/utils/legacyPins";

const PINNED_QUERY_KEY = ["pos-pinned-stores"] as const;

// Module-level so the one-time localStorage migration runs once per page load
// even under React StrictMode's double-effect invocation.
let legacyMigrationStarted = false;

/**
 * The shared, cross-user orderSpy watchlist (brief decision 1). Pins live in the
 * global public.pos_pinned_stores table — not in the browser — so the nightly
 * snapshot job can read them and every signed-in operator sees the same set.
 *
 * We persist store IDS only and re-resolve them against the live registry, so a
 * pinned name stays current and an id that vanishes from the registry drops off
 * the chips silently (its history still shows in the History tab). The toggle is
 * optimistic: the chip flips immediately and rolls back only if the write fails.
 *
 * On first load, any ids still under the old localStorage key are upserted into
 * the table and the key removed, so existing browser pins are not lost — but only
 * while the table is still empty. Once anyone has pinned server-side, a stale
 * browser's legacy key is simply dropped, so it can never re-pin stores that
 * another operator deliberately unpinned.
 */
export function useFavoriteStores(stores: readonly PosStore[]) {
  const getSupabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();
  const migrationRef = useRef(false);

  const { data: pinnedIds = [], isLoading } = useQuery<string[]>({
    queryKey: PINNED_QUERY_KEY,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      // pos_pinned_stores is not in the generated Database types (same untyped
      // pattern as usePosStores).
      const sb = supabase as unknown as SupabaseClient;
      const { data, error } = await sb
        .from("pos_pinned_stores")
        .select("store_id")
        .order("pinned_at", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((r: { store_id: string }) => r.store_id).filter(Boolean);
    },
    staleTime: 60 * 1000,
  });

  // One-time migration of legacy browser pins into the shared table.
  useEffect(() => {
    if (migrationRef.current || legacyMigrationStarted) return;
    const legacy = readLegacyPinIds();
    if (legacy.length === 0) return;
    migrationRef.current = true;
    legacyMigrationStarted = true;
    void (async () => {
      try {
        const supabase = await getSupabaseClient();
        if (!supabase) {
          // Not signed in yet: let a later load retry, so don't clear the key.
          legacyMigrationStarted = false;
          migrationRef.current = false;
          return;
        }
        const sb = supabase as unknown as SupabaseClient;
        const { count, error: countError } = await sb
          .from("pos_pinned_stores")
          .select("store_id", { count: "exact", head: true });
        if (countError) throw countError;
        if ((count ?? 0) > 0) {
          clearLegacyPins();
          return;
        }
        const { error } = await sb
          .from("pos_pinned_stores")
          .upsert(
            legacy.map((store_id) => ({ store_id })),
            { onConflict: "store_id", ignoreDuplicates: true },
          );
        if (error) throw error;
        clearLegacyPins();
        queryClient.invalidateQueries({ queryKey: PINNED_QUERY_KEY });
      } catch {
        // Leave the key in place so the (idempotent) migration retries next load.
        legacyMigrationStarted = false;
        migrationRef.current = false;
      }
    })();
  }, [getSupabaseClient, queryClient]);

  const idSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);

  const favorites = useMemo<PosStore[]>(() => {
    const byId = new Map(stores.map((s) => [s.store_id, s]));
    return pinnedIds.map((id) => byId.get(id)).filter((s): s is PosStore => Boolean(s));
  }, [pinnedIds, stores]);

  const mutation = useMutation({
    mutationFn: async ({ storeId, pin }: { storeId: string; pin: boolean }) => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;
      if (pin) {
        const { error } = await sb
          .from("pos_pinned_stores")
          .upsert({ store_id: storeId }, { onConflict: "store_id", ignoreDuplicates: true });
        if (error) throw error;
      } else {
        const { error } = await sb.from("pos_pinned_stores").delete().eq("store_id", storeId);
        if (error) throw error;
      }
    },
    onMutate: async ({ storeId, pin }) => {
      await queryClient.cancelQueries({ queryKey: PINNED_QUERY_KEY });
      const previous = queryClient.getQueryData<string[]>(PINNED_QUERY_KEY) ?? [];
      const next = pin
        ? previous.includes(storeId)
          ? previous
          : [...previous, storeId]
        : previous.filter((id) => id !== storeId);
      queryClient.setQueryData<string[]>(PINNED_QUERY_KEY, next);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(PINNED_QUERY_KEY, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PINNED_QUERY_KEY });
    },
  });

  const isFavorite = useCallback((storeId: string) => idSet.has(storeId), [idSet]);

  const toggle = useCallback(
    (store: PosStore) => {
      mutation.mutate({ storeId: store.store_id, pin: !idSet.has(store.store_id) });
    },
    [mutation, idSet],
  );

  return { favorites, pinnedIds, isFavorite, toggle, isLoading };
}
